import React from 'react';
import { Box, Typography } from '@mui/material';
import { ReplyToMessage, ChatMessage } from '../../../types/chat';

interface ReplyToDisplayProps {
  replyTo: ReplyToMessage;
  isCurrentUser: boolean;
  onImagePress?: (imageUrl: string, message?: any) => void;
}

const ReplyToDisplay: React.FC<ReplyToDisplayProps> = React.memo(({
  replyTo,
  isCurrentUser,
  onImagePress
}) => {
  const getMessageTypeDisplay = (messageType: string, messageText: string) => {
    switch (messageType) {
      case 'image':
        return '📷 Image';
      case 'video':
        return '🎬 Video';
      case 'audio':
        return '🎤 Voice message';
      case 'file':
      case 'pdf':
        return '📄 File';
      default:
        return messageText;
    }
  };

  const hasImageThumbnail = replyTo.messageType === 'image' && replyTo.imageUrl;
  const hasVideoThumbnail = replyTo.messageType === 'video' && replyTo.videoUrl;
  const hasAudioThumbnail = replyTo.messageType === 'audio' && replyTo.audioUrl;

  // Handle thumbnail clicks
  const handleThumbnailClick = (url: string) => {
    if (onImagePress) {
      // Create a proper ChatMessage object for the reply thumbnail
      const replyMessage: ChatMessage = {
        id: replyTo.messageId,
        messageType: replyTo.messageType as 'text' | 'audio' | 'image' | 'video' | 'file' | 'pdf' | 'reply',
        fromUID: replyTo.fromUser.uid,
        fromUser: {
          ...replyTo.fromUser,
          organizationId: '',
          role: 'client',
          FCMToken: '',
          type: 'client' as const,
          connectCode: '',
          authId: '',
        },
        groupId: '', // We don't have this from ReplyToMessage
        appDestination: 'messaging' as const,
        message: replyTo.messageText,
        createdOn: (() => {
          // Try parsing as different formats
          let date: Date;
          if (typeof replyTo.createdOn === 'string') {
            // Try ISO string first
            date = new Date(replyTo.createdOn);
            if (isNaN(date.getTime())) {
              // Try timestamp (milliseconds)
              const timestamp = parseInt(replyTo.createdOn);
              if (!isNaN(timestamp)) {
                date = new Date(timestamp);
                if (isNaN(date.getTime())) {
                  // Try timestamp in seconds
                  date = new Date(timestamp * 1000);
                }
              }
            }
          } else {
            date = new Date();
          }
          
          const finalDate = isNaN(date.getTime()) ? new Date() : date;
          return finalDate;
        })(),
        processed: true,
        viewedBy: [],
        imageUrl: replyTo.imageUrl,
        videoUrl: replyTo.videoUrl,
        audioUrl: replyTo.audioUrl,
        fileUrl: replyTo.fileUrl,
        pdfUrl: replyTo.pdfUrl,
      };
      onImagePress(url, replyMessage);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: isCurrentUser 
          ? 'rgba(255, 255, 255, 0.15)' 
          : 'rgba(0, 0, 0, 0.05)',
        borderLeft: `3px solid ${isCurrentUser ? '#ffffff' : '#2563EB'}`,
        borderRadius: '4px',
        p: 1,
        mb: 1,
        maxWidth: '100%',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: isCurrentUser ? 'rgba(255, 255, 255, 0.8)' : '#6B7280',
          fontWeight: 600,
          display: 'block',
          mb: 0.5,
        }}
      >
        {replyTo.fromUser.firstName} {replyTo.fromUser.lastName}
      </Typography>
      
      {/* Content area with potential image/video thumbnail */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 1
      }}>
        {/* Image thumbnail for image replies */}
        {hasImageThumbnail && (
          <Box
            component="img"
            src={replyTo.imageUrl}
            alt="Original image"
            onClick={() => replyTo.imageUrl && handleThumbnailClick(replyTo.imageUrl)}
            onError={(e) => {
              // Hide the image if it fails to load
              e.currentTarget.style.display = 'none';
            }}
            sx={{
              width: 48,
              height: 48,
              borderRadius: '4px',
              objectFit: 'cover',
              flexShrink: 0,
              border: `1px solid ${isCurrentUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          />
        )}
        
        {/* Video thumbnail for video replies */}
        {hasVideoThumbnail && (
          <Box
            onClick={() => replyTo.videoUrl && handleThumbnailClick(replyTo.videoUrl)}
            sx={{
              position: 'relative',
              width: 48,
              height: 48,
              borderRadius: '4px',
              overflow: 'hidden',
              flexShrink: 0,
              border: `1px solid ${isCurrentUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
              backgroundColor: '#000',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <video
              src={replyTo.videoUrl}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              muted
            />
            {/* Play icon overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                fontSize: '18px',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
              }}
            >
              ▶
            </Box>
          </Box>
        )}
        
        {/* Audio thumbnail for audio replies */}
        {hasAudioThumbnail && (
          <Box
            onClick={() => replyTo.audioUrl && handleThumbnailClick(replyTo.audioUrl)}
            sx={{
              position: 'relative',
              width: 48,
              height: 48,
              borderRadius: '4px',
              overflow: 'hidden',
              flexShrink: 0,
              border: `1px solid ${isCurrentUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
              backgroundColor: isCurrentUser ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {/* Audio icon */}
            <Box
              sx={{
                color: isCurrentUser ? 'rgba(255, 255, 255, 0.9)' : '#6B7280',
                fontSize: '24px',
              }}
            >
              🎵
            </Box>
          </Box>
        )}
        
        {/* Message text/type */}
        <Typography
          variant="body2"
          sx={{
            color: isCurrentUser ? 'rgba(255, 255, 255, 0.9)' : '#374151',
            fontSize: '0.875rem',
            fontStyle: replyTo.messageType !== 'text' ? 'italic' : 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            flex: 1,
          }}
        >
          {getMessageTypeDisplay(replyTo.messageType, replyTo.messageText)}
        </Typography>
      </Box>
    </Box>
  );
});

ReplyToDisplay.displayName = 'ReplyToDisplay';

export default ReplyToDisplay;
